import React from "react";
import { Container } from "../layouts/Common";
import tw from "twin.macro";
import styled from "styled-components";

type Entry = {
    /** "08" 형태. 생략하면 앞 항목과 같은 달의 연속 항목으로 본다. */
    month?: string;
    text: React.ReactNode;
};

type Group = { year: string; entries: Entry[] };

const HISTORY: Group[] = [
    {
        year: "2020",
        entries: [
            {
                month: "08",
                text: "케이유융합소프트웨어연구센터㈜ 설립(교원창업인가)"
            }
        ]
    },
    {
        year: "2021",
        entries: [
            { month: "03", text: "KU-Grant 프로그램 선정" },
            { month: "10", text: "기업부설연구소 개소" },
            {
                month: "12",
                text: "과학기술정통부 강소특구 특화성장 H-Bridge 선정"
            }
        ]
    },
    {
        year: "2022",
        entries: [
            { month: "04", text: "벤처기업인증" },
            {
                text: "‘애플리케이션 악성 코드 분석 시스템 및 방법’ 특허 등록"
            },
            {
                month: "05",
                text: "한국지능정보사회진흥원 인공지능 학습용 데이터 구축사업 선정"
            },
            { month: "11", text: "중소벤처기업부 창업성장기술개발사업 선정" },
            { text: "엔포유대학연합기술지주회사 투자 유치" },
            { month: "12", text: "2022년 병역지정업체(전문연구요원) 선정" },
            { text: "홍릉강소특구 연구소기업 선정" }
        ]
    },
    {
        year: "2023",
        entries: [
            {
                month: "04",
                text: "‘PCC 기반 동적 보안 검증 모듈을 이용한 결제 방법 및 장치’ 특허 출원"
            }
        ]
    },
    {
        year: "2024",
        entries: [
            {
                month: "08",
                text: "SCI 논문 게재 Kim, H. et al. Novel scheme for generating context-aware images using generative artificial intelligence. IEEE Access (2024)"
            },
            { text: "국내 최초 Agentforce Localization 연구개발" },
            {
                month: "10",
                text: "사옥 이전 (서울시 영등포구 문래북로 8, 1111, 1112호)"
            },
            {
                month: "11",
                text: "Salesforce AI & CX Festa Session ‘Einstein AI를 통한 고객 서비스 혁신 사례’"
            },
            {
                text: "Agentforce World Tour Seoul: Industry Innovation Session ‘Consumer Goods Cloud로 완성하는 Retail Execution의 미래’"
            },
            {
                month: "12",
                text: "Convergence Insight Summit 2024 Session ‘Agentforce를 통한 AI Powered Digital Workspace 사례’"
            },
            {
                text: "Global CRM 기업 Agentforce Certified(AI) Partner 지위 획득"
            }
        ]
    },
    {
        year: "2025",
        entries: [
            { month: "01", text: "Snowflake AI Data Cloud Partner 지위 획득" },
            {
                month: "03",
                text: "Agentforce Hacathon Demoday ‘AI Execution Excellence ’"
            },
            {
                text: "Salesforce Korea Partner Award 2025 ‘Trailblazer Partner of the Year’"
            },
            { month: "06", text: "Agentforce World Tour Korea Sponsors" },
            {
                text: "도서 출간 Agentic AI 시대 조직을 움직이는 새로운 엔진 (김평호, 김현조, 문준식, KUSRC 연구진 지음)"
            }
        ]
    }
];

const TimeUl = styled.ul`
    ${tw`relative mb-[5.5rem] w-full pt-12 sm:mb-16`}

    @media (min-width: 1600px) {
        margin-bottom: 11rem;
        padding-top: 0;
    }

    &:last-of-type {
        margin-bottom: 0;
    }
`;

const YearLabel = styled.span`
    position: absolute;
    top: 0;
    left: 12px;
    font: 700 30px/1 "Pretendard";
    color: #000;

    @media (min-width: 1600px) {
        top: -17px;
        left: -230px;
        font-size: 48px;
    }
`;

const TimeLi = styled.li<{ $first?: boolean }>`
    font: 400 17px/1 "Pretendard";
    color: #666;
    letter-spacing: -0.025em;
    margin-bottom: 1.58em;
    position: relative;
    padding-left: 109px;
    margin-left: 7px;
    word-break: keep-all;
    line-height: 125%;

    @media (max-width: 639px) {
        padding-left: 12px;
        margin-bottom: 12px;
    }

    ${props =>
        props.$first &&
        `
        &::after {
            content: "";
            width: 55px;
            height: 15px;
            position: absolute;
            top: 0;
            left: -54px;
            display: block;
            background: url("img/history-bullet.svg");
        }
    `}
`;

const DateLabel = styled.span<{ $first?: boolean }>`
    position: absolute;
    top: -2px;
    left: 11px;
    font: 700 21px/1 "Pretendard";
    color: #000;
    display: block;

    @media (max-width: 639px) {
        position: static;
        font: 600 19px/1 "Pretendard";
        padding-top: ${props => (props.$first ? "0" : "12px")};
        margin-bottom: 6px;
    }
`;

const EntryText = styled.span`
    display: block;

    @media (max-width: 639px) {
        text-wrap: balance;
    }
`;

const Timeline = () => {
    return (
        <Container>
            <div className="relative flex w-full flex-col sm:pt-24 sm:pb-12">
                <div className="w-full border-l pt-12 sm:pt-0">
                    {HISTORY.map(group => (
                        <TimeUl key={group.year}>
                            <YearLabel>
                                <span className="sm:hidden">{group.year}</span>
                                <span className="hidden sm:inline">
                                    {group.year}&apos;s
                                </span>
                            </YearLabel>

                            {group.entries.map((entry, i) => (
                                <TimeLi
                                    key={`${group.year}-${i}`}
                                    $first={i === 0}
                                >
                                    {entry.month && (
                                        <DateLabel $first={i === 0}>
                                            <span className="sm:hidden">
                                                {Number(entry.month)}월
                                            </span>
                                            <span className="hidden sm:inline">
                                                {group.year}. {entry.month}
                                            </span>
                                        </DateLabel>
                                    )}
                                    <EntryText>{entry.text}</EntryText>
                                </TimeLi>
                            ))}
                        </TimeUl>
                    ))}
                </div>
            </div>
        </Container>
    );
};

export default Timeline;
